// src/components/Dashboard/Dashboard.jsx
import React, { useState, useEffect } from "react";
import "./Dashboard.css";
import BarChartComponent from "./BarChartComponent";
import PieChartSummary from "./PieChartSummary";
import BookingsTable from "./BookingsTable";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";
import { API_URL } from "../../utils/config";

const baseUrl = API_URL;

const Dashboard = () => {
  const { t } = useTranslation("dashboard");
  const [bookings, setBookings] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // New filter states lifted from the child component.
  const [filterYear, setFilterYear] = useState("");
  const [filterMonth, setFilterMonth] = useState("");

  // Retrieve user from local storage and parse it
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  // Function to update filters coming from the child component.
  const handleFilterChange = (year, month) => {
    setFilterYear(year);
    setFilterMonth(month);
  };

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  useEffect(() => {
    // Retrieve token from local storage
    const chart = true;

    // Build URL with query parameters for chart, year, and month (if provided)
    let url = new URL(`${baseUrl}/api/v1/attnd-supplier/suppliers/orders`);
    const params = new URLSearchParams({ chart: chart.toString() });
    if (filterYear) {
      params.append("year", filterYear);
    }
    if (filterMonth) {
      params.append("month", filterMonth);
    }
    url.search = params.toString();

    fetch(url.toString(), {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        lang: getLangFromLocalStorage(),
      },
    })
      .then((response) => {
        // If the response status is 401, remove token and user, then redirect
        if (response.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          window.location.href = "/login";
          throw new Error("Unauthorized");
        }
        if (!response.ok) {
          return response.json().then((errData) => {
            throw new Error(errData.error || "Network response was not ok");
          });
        }
        return response.json();
      })
      .then((data) => {
        if (data) {
          if (data.data) {
            setBookings(data.data);
          }
          if (data.statistics) {
            setStatistics(data.statistics);
          }
          if (data.monthlyData) {
            setMonthlyData(data.monthlyData);
          }
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching bookings:", error);
        setError(error);
        setLoading(false);
      });
    // Re-run fetch whenever filterYear or filterMonth change.
  }, [filterYear, filterMonth]);

  if (loading) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        {t("error")}: {error.message}
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <h1 className="dashboard-title">
        {t("greeting")}{" "}
        <span className="dashboard-highlight">
          {user ? `${user.firstName} ${user.familyName}` : "User"}
        </span>
        !
      </h1>

      <div className="dashboard-grid">
        {/* Statistics Card with BarChart */}
        <div className="dashboard-card dashboard-chart">
          <BarChartComponent
            statistics={statistics}
            monthlyData={monthlyData}
            onFilterChange={handleFilterChange} // Pass filter change callback to child component.
          />
        </div>

        {/* Summary Card with PieChart */}
        <div className="dashboard-card dashboard-summary">
          <div
            className="dashboard-summary"
            style={{ width: "100%", minHeight: 300 }}
          >
            <PieChartSummary statistics={statistics} />
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <BookingsTable bookings={bookings} />
    </div>
  );
};

export default Dashboard;
